import {
  Component,
  Input,
  OnInit,
  ElementRef,
  ViewChild,
  OnDestroy,
} from '@angular/core';
import * as pdfjsLib from 'pdfjs-dist';
import { SafeResourceUrl } from '@angular/platform-browser';

@Component({
  selector: 'app-pdf-viewer',
  templateUrl: './pdf-viewer.component.html',
  styleUrls: ['./pdf-viewer.component.scss'],
})
export class PdfViewerComponent implements OnInit, OnDestroy {
  @Input() pdfUrl!: string | SafeResourceUrl;
  @Input() width: string = '100%';
  @Input() height: string = '100%';
  @Input() containerHeight: string = '80vh';
  @Input() canPrint: boolean = false;
  @Input() canDownload: boolean = false;
  @Input() canZoom: boolean = false;

  @ViewChild('pdfContainer') pdfContainer!: ElementRef;

  private pdfDoc: any;
  private objectUrl: string | null = null;
  private pdfBlob: Blob | null = null;
  public errorMessage: string | null = null;
  public numPages: number = 0;
  public pageCanvases: HTMLCanvasElement[] = [];
  private pageWrappers: HTMLDivElement[] = [];
  public scale: number = 1.0;

  constructor() {
    pdfjsLib.GlobalWorkerOptions.workerSrc = './assets/pdf.worker.min.js';
  }

  private isValidBlobUrl(url: string | SafeResourceUrl): boolean {
    if (!url) return false;
    const str = typeof url === 'string' ? url : (url as any).changingThisBreaksApplicationSecurity || '';
    return typeof str === 'string' && str.startsWith('blob:');
  }

  ngOnInit() {
    if (this.isValidBlobUrl(this.pdfUrl)) {
      this.loadPDF();
    }
  }

  private async loadPDF() {
    try {
      let url: string;
      if (typeof this.pdfUrl === 'string') {
        url = this.pdfUrl;
      } else {
        url = (this.pdfUrl as any).changingThisBreaksApplicationSecurity || '';
      }

      if (!this.isValidBlobUrl(url)) {
        this.errorMessage = 'Invalid PDF URL format';
        return;
      }

      if (!url || typeof url !== 'string' || url.trim() === '') {
        this.errorMessage = 'PDF URL is empty or invalid';
        return;
      }

      const response = await fetch(url);
      if (!response.ok) {
        this.errorMessage = 'Failed to load PDF: Server returned an error';
        return;
      }

      const blob = await response.blob();
      if (blob.size === 0) {
        this.errorMessage = 'PDF file is empty';
        return;
      }

      this.pdfBlob = blob;
      this.objectUrl = URL.createObjectURL(blob);

      this.pdfDoc = await pdfjsLib.getDocument(this.objectUrl).promise;
      this.numPages = this.pdfDoc.numPages;
      this.errorMessage = null;
      await this.renderAllPages();
    } catch (error: any) {
      if (error?.message?.includes('Failed to fetch')) {
        this.errorMessage = 'The PDF URL is no longer valid or has expired';
      } else {
        this.errorMessage = error?.message || 'The PDF could not be loaded. Please try again later.';
      }
    }
  }

  private async renderAllPages() {
    this.pageCanvases = [];
    this.pageWrappers = [];
    for (let num = 1; num <= this.numPages; num++) {
      const page = await this.pdfDoc.getPage(num);
      const viewport = page.getViewport({ scale: this.scale });
      const canvas = document.createElement('canvas');
      const wrapper = document.createElement('div');
      wrapper.className = 'pdf-page-wrapper';

      wrapper.style.marginTop = '10px';
      wrapper.style.marginBottom = '10px';
      wrapper.style.width = '100%';
      wrapper.style.display = 'flex';
      wrapper.style.justifyContent = 'center';

      canvas.className = 'pdf-canvas';
      canvas.style.display = 'block';
      canvas.style.maxWidth = '100%';
      canvas.style.height = 'auto';
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      await page.render({
        canvasContext: canvas.getContext('2d'),
        viewport: viewport,
      }).promise;
      wrapper.appendChild(canvas);
      this.pageCanvases.push(canvas);
      this.pageWrappers.push(wrapper);
    }
    this.renderCanvases();
  }

  private renderCanvases() {
    if (!this.pdfContainer) return;
    const container = this.pdfContainer.nativeElement;

    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }

    for (const wrapper of this.pageWrappers) {
      container.appendChild(wrapper);
    }
  }

  public zoomIn() {
    this.scale *= 1.2;
    this.renderAllPages();
  }

  public zoomOut() {
    this.scale /= 1.2;
    this.renderAllPages();
  }

  public printPDF() {
    if (!this.pageCanvases.length) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.write(
      '<html><head><title>Print PDF</title></head><body style="margin:0;">'
    );
    for (const canvas of this.pageCanvases) {
      const imgData = canvas.toDataURL();
      printWindow.document.write(
        `<img src="${imgData}" style="display:block; margin: 0 auto 24px auto; width:100%; max-width:816px; background:#fff; box-shadow:0 2px 8px rgba(0,0,0,0.08);">`
      );
    }
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  }

  public downloadPDF() {
    if (!this.pdfBlob) return;
    const a = document.createElement('a');
    a.href = URL.createObjectURL(this.pdfBlob);
    a.download = 'document.pdf';
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  }

  ngOnDestroy() {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
  }
}
